import { useEffect, useState } from "react";
import { Checkbox, Collapse, Space, Tooltip, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import i18next, { t } from "i18next";
import ApiService from "../../core/service/ApiService";
import usePermissionGuard from "./usePermissionGuard";

const { Panel } = Collapse;
/**
 * @param disabled Tüm kutular salt okunur (ör. yönetilemeyen rol/kullanıcı).
 * Kendinde olmayan izinler her durumda pasiftir: kimse sahip olmadığı izni veremez/kaldıramaz
 * (süper admin hariç; sunucu da aynı kuralı uygular).
 */
export default function PermissionGroup({ selectedPermissions = [], onChangeSelectedPermissions, disabled = false }) {
    const [groups, setGroup] = useState([])
    const [permissions, setPermissions] = useState([])
    const { canGrantPermission } = usePermissionGuard();
    const isLocked = (permission) => disabled || !canGrantPermission(permission.id);

    const getGroups = async () => {
        const response = await ApiService.Role.GetPermissionGroups();
        if (response.success) {
            // İzni olmayan grup (ör. Patient) boş panel olarak görünmesin
            setGroup(response.data.filter(group => group.permissions.length > 0))

            let permissions = [];
            response.data.forEach(group => {
                permissions = [...permissions, ...group.permissions.map(p => {
                    return { ...p, groupName: group.name };
                })]
            })
            setPermissions(permissions)
        }
    }

    const getDependencies = (permission) => {
        return permissions.filter(p => permission.dependencies.includes(p.id))
    }

    const onCheckedPermission = (checked, permission) => {
        if (checked) {
            onSelect([...selectedPermissions, permission.id])
        }
        else {
            onSelect(selectedPermissions.filter(p => p !== permission.id))
        }
    }

    // Grubun tamamını tek hamlede aç/kapat. Yalnız o grubun izinlerine dokunur;
    // diğer gruplardan seçili olanlar korunur.
    const onCheckedGroup = (checked, group) => {
        // Kilitli izinler (sahip olunmayanlar) grup seçiminde de olduğu gibi kalır
        const groupIds = group.permissions.filter(p => !isLocked(p)).map(p => p.id);

        if (checked) {
            onSelect([...selectedPermissions, ...groupIds])
        }
        else {
            onSelect(selectedPermissions.filter(id => !groupIds.includes(id)))
        }
    }

    const onSelect = (data) => {
        onChangeSelectedPermissions && onChangeSelectedPermissions([...new Set(data)])
    }

    useEffect(() => {
        getGroups()
    }, [])

    const groupHeader = (group) => {
        const groupIds = group.permissions.map(p => p.id);
        const selectedCount = groupIds.filter(id => selectedPermissions.includes(id)).length;

        return (
            <Space>
                <Checkbox
                    checked={selectedCount > 0 && selectedCount === groupIds.length}
                    indeterminate={selectedCount > 0 && selectedCount < groupIds.length}
                    disabled={group.permissions.every(isLocked)}
                    // Panel başlığındaki tıklama paneli açıp kapatır; kutunun tıklaması oraya sızmasın
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => onCheckedGroup(e.target.checked, group)}
                >
                    {t(`PERMISSION_GROUP_${group.name}`, group.name)}
                </Checkbox>
                <Typography.Text type={'secondary'}>{`${selectedCount}/${groupIds.length}`}</Typography.Text>
            </Space>
        )
    }

    return (
        <Collapse expandIcon={() => { }}>
            {
                groups.map(group => {
                    return (
                        <Panel
                            key={group.id + 'g'}
                            header={groupHeader(group)}
                        >
                            {
                                group.permissions.map(p => {
                                    return (
                                        <div key={p.id + 'pg'} style={{ marginBottom: 8, borderBottom: '1px solid #e0e0e0', paddingBottom: 8 }}>
                                            <Checkbox
                                                id={p.id + 'pg'}
                                                onChange={(e) => { onCheckedPermission(e.target.checked, p) }}
                                                checked={selectedPermissions.findIndex(x => x === p.id) > -1}
                                                disabled={isLocked(p)}
                                            >
                                                <Tooltip title={!disabled && !canGrantPermission(p.id) ? t('You cannot grant a permission you do not have.') : undefined}>
                                                    <b>{t(p.name)}</b>
                                                </Tooltip>
                                            </Checkbox>
                                            {/* İznin ne yaptığı: ? ikonunun ipucunda (tr.json / en.json'da <İZİN>_DESC).
                                                Kutunun dışında durur ki ikona dokunmak izni seçmesin. */}
                                            {
                                                i18next.exists(`${p.name}_DESC`) && (
                                                    <Tooltip title={t(`${p.name}_DESC`)}>
                                                        <QuestionCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
                                                    </Tooltip>
                                                )
                                            }
                                        </div>
                                    )
                                })
                            }
                        </Panel>
                    )
                })
            }
        </Collapse>
    )
}
